import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Instagram, Send, MessageCircle } from "lucide-react";

const LOGO_URL = "/logo-gv.png";
const HIDDEN_PATHS = ["/login"];

const SOCIALS = [
  { key: "social_instagram", label: "Instagram", Icon: Instagram },
  { key: "social_telegram", label: "Telegram", Icon: Send },
  { key: "social_whatsapp", label: "WhatsApp", Icon: MessageCircle },
];

export default function Footer() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  if (HIDDEN_PATHS.includes(location.pathname)) return null;

  const activeSocials = SOCIALS.filter(s => settings[s.key]);

  const navLinks = isAuthenticated
    ? [
        { to: "/dashboard", label: "Dashboard" },
        { to: "/chi-siamo", label: "Chi Siamo" },
        { to: "/servizi", label: "Percorso e Servizi" },
        { to: "/strumenti", label: "Strumenti" },
        { to: "/link-utili", label: "Link Utili" },
      ]
    : [
        { to: "/", label: "Home" },
        { to: "/diventa-studente", label: "Diventa Studente" },
        { to: "/login", label: "Accedi" },
      ];

  return (
    <footer className="relative mt-16" data-testid="site-footer" style={{ backgroundColor: "var(--mm-surface)", borderTop: "1px solid var(--mm-border)" }}>
      {/* Social band */}
      <div className="container py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-5" style={{ borderBottom: "1px solid var(--mm-border)" }}>
        <p className="text-lg sm:text-xl font-extrabold uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)", letterSpacing: "0.05em" }}>
          Seguici sui social
        </p>
        <div className="flex items-center gap-3" data-testid="footer-socials">
          {activeSocials.map(({ key, label, Icon }) => (
            <a
              key={key}
              href={settings[key]}
              target="_blank"
              rel="noopener noreferrer"
              className="social-btn"
              aria-label={label}
              title={label}
              data-testid={`footer-${key}`}
            >
              <Icon size={19} />
            </a>
          ))}
          {activeSocials.length === 0 && (
            <span className="text-xs" style={{ color: "var(--mm-text-muted)" }}>Presto disponibili</span>
          )}
        </div>
      </div>

      {/* Columns */}
      <div className="container py-10 sm:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 text-center sm:text-left">
          <div className="flex flex-col items-center sm:items-start">
            <img src={LOGO_URL} alt="GoldVision" className="w-16 h-16 object-contain mb-4" />
            <p className="text-sm leading-relaxed max-w-xs" style={{ color: "var(--mm-text-secondary)" }}>
              Formazione e strumenti per trader e investitori.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.22em", color: "var(--mm-gold)" }}>
              Navigazione
            </h4>
            <ul className="space-y-2.5">
              {navLinks.map(l => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm transition-colors hover:text-teal-400" style={{ color: "var(--mm-text-secondary)" }}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.22em", color: "var(--mm-gold)" }}>
              Disclaimer
            </h4>
            <p className="text-xs leading-relaxed" style={{ color: "var(--mm-text-muted)" }}>
              I contenuti hanno esclusivamente scopo educativo e informativo e non costituiscono consulenza finanziaria. Il trading comporta rischi elevati: il capitale investito può essere perso interamente. Le performance passate non garantiscono risultati futuri.
            </p>
          </div>
        </div>
      </div>

      {/* Giant brand mark */}
      <div className="overflow-hidden" style={{ borderTop: "1px solid var(--mm-border)" }}>
        <div className="container pt-8 pb-6 text-center">
          <p
            className="display-title shimmer-gold select-none"
            style={{ fontSize: "clamp(2.2rem, 8vw, 6.5rem)" }}
            aria-hidden="true"
          >
            GoldVision
          </p>
          <p className="text-xs mt-4" style={{ color: "var(--mm-text-muted)" }}>
            © {new Date().getFullYear()} GoldVision. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </footer>
  );
}
